import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, MapPin, Upload, FileCheck2, FileX2, Eye, Lock, Languages, Zap, CalendarClock, CalendarRange, Sprout, Star, Award, Trophy, CheckCircle2, Loader2, Bell } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useT } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { DISTRICTS, CATEGORIES, HOURS, EXPERIENCE, AVAILABILITY, LANGUAGES } from "../lib/options";
import { ChoiceCard, HourCard, Progress, Spinner } from "../components/Bits";
import { Photo } from "../components/Chrome";
import { AuthBox } from "./Auth";

const EXP_ICONS = { none: Sprout, lt1: Star, "1to3": Award, gt3: Trophy };
const AV_ICONS = { now: Zap, "2weeks": CalendarClock, "1month": CalendarRange };
const DRAFT = "wis_profile_draft";
const TOTAL = 8;

const toggle = (arr = [], v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

function Intro() {
  const { t } = useT();
  return (
    <div className="page grid items-center gap-8 py-8 md:grid-cols-2">
      <Photo k="cashier" eager className="h-48 w-full rounded-[2rem] sm:h-72 md:order-2 md:h-[420px]" />
      <div>
        <h1 className="text-4xl font-extrabold tracking-tight text-[color:var(--navy)] sm:text-5xl">{t("reg_intro_t")}</h1>
        <p className="mt-3 text-lg text-slate-600">{t("reg_intro_sub")}</p>
        <div className="mt-8"><AuthBox next="/start" role="candidate" testid="wizard-auth" /></div>
      </div>
    </div>
  );
}

function CvStep({ p, setP }) {
  const { t } = useT();
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/candidate/cv", fd);
      setP((x) => ({ ...x, cv_path: data.cv_path, no_cv: false }));
      toast.success(t("w_cv_ok"));
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const remove = async () => { await api.delete("/candidate/cv").catch(() => {}); setP((x) => ({ ...x, cv_path: null })); };
  return (
    <div className="space-y-3">
      <input ref={ref} type="file" accept=".pdf,.doc,.docx,image/*" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="wizard-cv-file-input" />
      {p.cv_path ? (
        <div className="flex items-center gap-3 rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-4" data-testid="wizard-cv-uploaded">
          <FileCheck2 className="text-emerald-600" size={28} /><span className="flex-1 font-bold text-emerald-800">{t("w_cv_ok")}</span>
          <button onClick={remove} className="min-h-[44px] rounded-xl px-3 font-semibold text-red-600" data-testid="wizard-cv-remove-btn">{t("w_cv_remove")}</button>
        </div>
      ) : (
        <ChoiceCard icon={busy ? Loader2 : Upload} label={t("w_cv_upload")} onClick={() => !busy && ref.current.click()} testid="wizard-cv-upload-card" />
      )}
      {!p.cv_path && <ChoiceCard icon={FileX2} label={t("w_cv_none")} selected={p.no_cv} onClick={() => setP((x) => ({ ...x, no_cv: !x.no_cv }))} testid="wizard-cv-none-card" />}
    </div>
  );
}

export default function CandidateWizard() {
  const { t, lang } = useT();
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [p, setP] = useState(null);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    if (user?.role !== "candidate") return;
    api.get("/candidate/profile").then(({ data }) => {
      const draft = JSON.parse(localStorage.getItem(DRAFT) || "null");
      const merged = draft?.user_id === user.id ? { ...data, ...draft.p } : data;
      setP({ ...merged, full_name: merged.full_name || user.name || "", visible_to_employers: merged.visible_to_employers ?? true, job_alerts: merged.job_alerts ?? true, whatsapp_ok: merged.whatsapp_ok ?? true });
      setStep(draft?.user_id === user.id ? draft.step : 1);
    }).catch((e) => toast.error(errMsg(e)));
  }, [user]);

  useEffect(() => {
    if (p && user) localStorage.setItem(DRAFT, JSON.stringify({ user_id: user.id, step, p }));
  }, [p, step, user]);

  if (loading) return <Spinner />;
  if (!user) return <Intro />;
  if (!user.role) return <Navigate to="/choose-role" replace />;
  if (user.role !== "candidate") return <Navigate to="/" replace />;
  if (!p) return <Spinner />;

  const valid = {
    1: p.full_name?.trim().length > 1 && (p.phone || "").replace(/\D/g, "").length >= 7,
    2: !!p.location, 3: (p.job_types || []).length > 0, 4: !!p.experience, 5: (p.hours || []).length > 0,
    6: !!p.availability, 7: !!p.cv_path || !!p.no_cv, 8: true,
  }[step];

  const save = async (extra = {}) => {
    const { no_cv, cv_path, ai_cv, user_id, consent_at, updated_at, hidden_by_admin, ...rest } = p;
    await api.put("/candidate/profile", { ...rest, lang, ...extra });
  };

  const next = async () => {
    setBusy(true);
    try {
      if (step === TOTAL) {
        await save({ completed: true });
        localStorage.removeItem(DRAFT);
        setFinished(true);
      } else {
        await save();
        setStep(step + 1);
        window.scrollTo(0, 0);
      }
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (finished) {
    const target = sp.get("next");
    return (
      <div className="narrow flex flex-col items-center py-14 text-center fade-up" data-testid="wizard-done">
        <CheckCircle2 size={80} className="text-emerald-500" />
        <h1 className="mt-4 text-3xl font-bold text-[color:var(--navy)]">{t("w_done_t")}</h1>
        <p className="mt-2 text-lg text-slate-600">{t("w_done_sub")}</p>
        <button className="btn-orange mt-8 !w-full" onClick={() => nav(target || "/jobs")} data-testid="wizard-done-continue-btn">{target ? t("apply") : t("see_jobs")}<ArrowRight size={22} /></button>
        <Link to="/me/cv" className="btn-ghost mt-3 !w-full" data-testid="wizard-done-cv-btn">{t("ai_cv_btn")}</Link>
      </div>
    );
  }

  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));
  const titles = { 1: "w_name_t", 2: "w_loc_t", 3: "w_types_t", 4: "w_exp_t", 5: "w_hours_t", 6: "w_avail_t", 7: "w_cv_t", 8: "w_vis_t" };
  const helps = { 3: "w_types_help", 4: "w_exp_help", 5: "w_hours_help", 7: "w_cv_help" };

  return (
    <div className="narrow pb-40 pt-6" data-testid={`wizard-step-${step}`}>
      <Progress step={step} total={TOTAL} />
      <h1 className="mt-6 text-3xl font-bold leading-tight text-[color:var(--navy)]">{t(titles[step])}</h1>
      {helps[step] && <p className="mt-2 text-slate-600">{t(helps[step])}</p>}
      <div className="mt-6 space-y-3">
        {step === 1 && (
          <>
            <label className="label" htmlFor="w-name">{t("w_name")}</label>
            <input id="w-name" className="field" value={p.full_name || ""} onChange={(e) => set("full_name", e.target.value)} autoComplete="name" data-testid="wizard-name-input" />
            <label className="label pt-3" htmlFor="w-phone">{t("w_phone")}</label>
            <input id="w-phone" className="field" type="tel" inputMode="tel" placeholder="5XXX XXXX" value={p.phone || ""} onChange={(e) => set("phone", e.target.value)} autoComplete="tel" data-testid="wizard-phone-input" />
            <p className="text-sm text-slate-500">{t("w_phone_help")}</p>
            <ChoiceCard multi label={t("w_wa")} selected={p.whatsapp_ok} onClick={() => set("whatsapp_ok", !p.whatsapp_ok)} testid="wizard-whatsapp-toggle" />
          </>
        )}
        {step === 2 && DISTRICTS.map((d) => <ChoiceCard key={d} icon={MapPin} label={d} selected={p.location === d} onClick={() => set("location", d)} testid={`wizard-loc-${d.replace(/\s/g, "-").toLowerCase()}`} />)}
        {step === 3 && CATEGORIES.map(({ v, icon }) => <ChoiceCard multi key={v} icon={icon} label={t(`cat_${v}`)} selected={(p.job_types || []).includes(v)} onClick={() => set("job_types", toggle(p.job_types, v))} testid={`wizard-type-${v}`} />)}
        {step === 4 && EXPERIENCE.map((v) => <ChoiceCard key={v} icon={EXP_ICONS[v]} label={t(`e_${v}`)} selected={p.experience === v} onClick={() => set("experience", v)} testid={`wizard-exp-card-${v}`} />)}
        {step === 5 && HOURS.map(({ v, icon }) => <HourCard key={v} icon={icon} label={t(`h_${v}`)} help={t(`h_${v}_d`)} selected={(p.hours || []).includes(v)} onClick={() => set("hours", toggle(p.hours, v))} testid={`wizard-hours-checkbox-${v}`} />)}
        {step === 6 && (
          <>
            {AVAILABILITY.map((v) => <ChoiceCard key={v} icon={AV_ICONS[v]} label={t(`a_${v}`)} selected={p.availability === v} onClick={() => set("availability", v)} testid={`wizard-avail-${v}`} />)}
            <h2 className="pt-6 text-xl font-bold text-[color:var(--navy)]">{t("w_lang_t")}</h2>
            <div className="grid grid-cols-2 gap-3">
              {LANGUAGES.map((v) => <ChoiceCard multi key={v} icon={Languages} label={t(`l_${v}`)} selected={(p.languages || []).includes(v)} onClick={() => set("languages", toggle(p.languages, v))} testid={`wizard-lang-${v}`} />)}
            </div>
          </>
        )}
        {step === 7 && <CvStep p={p} setP={setP} />}
        {step === 8 && (
          <>
            <ChoiceCard icon={Eye} label={t("w_vis_yes")} sub={t("w_vis_yes_d")} selected={p.visible_to_employers} onClick={() => set("visible_to_employers", true)} testid="wizard-visibility-public" />
            <ChoiceCard icon={Lock} label={t("w_vis_no")} sub={t("w_vis_no_d")} selected={!p.visible_to_employers} onClick={() => set("visible_to_employers", false)} testid="wizard-visibility-private" />
            <h2 className="pt-6 text-xl font-bold text-[color:var(--navy)]">{t("alerts_t")}</h2>
            <ChoiceCard multi icon={Bell} label={t("alerts_label")} sub={t("alerts_sub")} selected={p.job_alerts} onClick={() => set("job_alerts", !p.job_alerts)} testid="wizard-job-alerts-toggle" />
          </>
        )}
      </div>
      <div className="fixed inset-x-0 bottom-16 z-30 border-t border-slate-200 bg-white/95 p-4 backdrop-blur md:bottom-0">
        <div className="mx-auto flex max-w-xl gap-3">
          {step > 1 && <button className="btn-ghost !w-auto !px-5" onClick={() => setStep(step - 1)} data-testid="wizard-step-prev" aria-label={t("back")}><ArrowLeft size={22} /><span className="hidden min-[400px]:inline">{t("back")}</span></button>}
          <button className="btn-orange flex-1" disabled={!valid || busy} onClick={next} data-testid="wizard-step-next">
            {busy ? <Loader2 className="animate-spin" /> : step === TOTAL ? t("finish") : t("next")}<ArrowRight size={22} />
          </button>
        </div>
        <p className="mt-1 text-center text-xs text-slate-500">{t("saved_auto")}</p>
      </div>
    </div>
  );
}
